import { FC } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import { HoldingClass, Range } from 'types';

import { formatNumber, formatNumberDecimals } from 'utils/formats';

import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import TableRow from 'modules/_shared/Table/components/TableRow';
import { RowCell } from 'modules/_shared/Table/types/RowCell';

import { useHoldingClassListPage } from '../context/HoldingClassListPageContext';
import { getMarginSeparator } from '../utils/getMarginSeparator';
import { useGetHoldingClassRowData } from '../hooks/useGetHoldingClassRowData';
import RangeRowLine from './RangeRowLine';

type HoldingClassTableRowProps = {
  index: number;
  holdingClass: HoldingClass;
};

const HoldingClassTableRow: FC<HoldingClassTableRowProps> = ({
  index,
  holdingClass,
}) => {
  const { t } = useTranslate();

  const { data, actions } = useHoldingClassListPage();
  const { data: rowData } = useGetHoldingClassRowData({
    holdingClass,
    societyNominalValue: data.societyNominalValue,
  });

  const handleView = () => actions.handleViewClass(index);
  const handleEdit = () => actions.handleEditClass(index);

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`holding-class-view-option-${index}`}
      onClick={handleView}
    >
      <em className="icon ni ni-eye fs-20px" />
      <span className="ml-1">{t('See')}</span>
    </a>,
    <a
      className="d-flex cursor-pointer"
      key={`holding-class-edit-option-${index}`}
      onClick={handleEdit}
    >
      <em className="icon ni ni-edit fs-20px" />
      <span className="ml-1">{t('Edit')}</span>
    </a>,
  ];

  const hasOptions = data.isAdmin && !data.isReadOnly;

  const cells = [
    {
      key: `holding-class-name-${index}`,
      content: (
        <span className="text-primary font-weight-bold">{rowData.name}</span>
      ),
    },
    {
      key: `holding-class-nominal-value-${index}`,
      content: formatNumberDecimals(rowData.nominalValue),
      textPosition: 'right',
    },
    {
      key: `holding-class-shares-count-${index}`,
      content: formatNumber(rowData.sharesCount),
      textPosition: 'right',
    },
    {
      key: `holding-class-numeration-value-${index}`,
      content: (
        <>
          {rowData.ranges
            ?.slice(0, 5)
            .map((range: Range, rangeIndex: number) => (
              <div
                key={`${index}-${range.from}-${range.to}`}
                style={getMarginSeparator(rangeIndex)}
              >
                <RangeRowLine range={range} />
              </div>
            ))}
          {rowData.ranges.length > 5 && (
            <div className="mt-1 ff-italic text-muted fs-12px">
              ...
              {t('AndXNumerationsMore', {
                count: formatNumber(rowData.ranges.length - 5),
              })}
            </div>
          )}
        </>
      ),
      textPosition: 'right',
      hideOnMobile: true,
    },
    {
      key: `holding-class-votes-${index}`,
      content: rowData.votes,
      textPosition: 'right',
      hideOnMobile: true,
    },
    {
      key: `holding-class-numeration-${index}`,
      content: rowData.numerationLabel,
      hideOnMobile: true,
    },
    {
      key: `holding-class-liquidation-${index}`,
      content: rowData.liquidationLabel,
      hideOnMobile: true,
    },
    {
      key: `holding-class-antidilution-${index}`,
      content: rowData.antidilutionLabel,
      hideOnMobile: true,
    },
    hasOptions
      ? {
          key: `holding-class-options-${index}`,
          content: <ToggleMenu toggleOptions={toggleOptions} />,
          textPosition: 'center',
        }
      : null,
  ].filter(Boolean) as RowCell[];

  return <TableRow cells={cells} />;
};

export default HoldingClassTableRow;
